import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeftRight, Clock3, CreditCard, KeyRound, Network, Server, ShieldCheck } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  getCaptecPerformanceProfile,
  releaseCompletedScrollPin,
} from "./scrollPerformance";

gsap.registerPlugin(ScrollTrigger);

const VIDEO_SRC = "/assets/videos/captec-showcase.mp4";
const VIDEO_POSTER = "/assets/videos/captec-showcase-poster.jpg";
const PHONE_FRAME_SRC = "/assets/devices/captec-phone-landscape-frame.webp";

const VideoMorphShowcase = ({ enabled = true }) => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const videoAvailableRef = useRef(false);
  const videoStartedRef = useRef(false);
  const [videoError, setVideoError] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) return undefined;

    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return undefined;

    const profile = getCaptecPerformanceProfile();
    const { lowResource, reducedMotion } = profile;
    let videoRequested = false;

    const isSectionVisible = () => {
      const rect = section.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    };

    const playVideoOnce = () => {
      if (
        !videoAvailableRef.current ||
        videoStartedRef.current ||
        !isSectionVisible() ||
        document.hidden ||
        video.ended
      ) {
        return;
      }
    
      videoStartedRef.current = true;
      video.currentTime = 0;
    
      const playPromise = video.play();
    
      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Permite un nuevo intento si el navegador bloquea el autoplay.
          videoStartedRef.current = false;
        });
      }
    };
    
    const resumeStartedVideo = () => {
      if (
        !videoStartedRef.current ||
        !isSectionVisible() ||
        video.ended ||
        document.hidden ||
        !video.paused
      ) {
        return;
      }
    
      video.play().catch(() => {
        // No reiniciar. Solo continuar desde el punto actual.
      });
    };
    
    const pauseForHiddenTab = () => {
      if (!video.paused && !video.ended) {
        video.pause();
      }
    };
    
    const handleCanPlay = () => {
      videoAvailableRef.current = true;
      setVideoError(false);
      playVideoOnce();
    };
    
    video.addEventListener("canplay", handleCanPlay);

    const ctx = gsap.context(() => {
      const device = section.querySelector(".video-morph-device");
      const screen = section.querySelector(".video-morph-screen");
      const frame = section.querySelector(".video-morph-frame");
      const copy = section.querySelector(".video-morph-copy");
      const badges = section.querySelectorAll(".video-morph-badges > *");

      if (!device || !screen || !frame || !copy) return;

      const finalRadius = lowResource ? "38px" : "48px";
      const finalScreenInset = "5.4% 5.1%";
      const finalScreenRadius = "58px";
      // The frame artwork has a small transparent perimeter. Starting the
      // screen at that perimeter prevents the video from escaping around the
      // phone while the device is shrinking.
      const initialScreenInsetX = 0.034;
      const initialScreenInsetY = 0.022;
      const initialScreenInset = "2.2% 3.4%";
      const getInitialGeometry = () => {
        const navbarHeight = window.innerWidth >= 1024 ? 80 : 64;
        const baseWidth = Math.max(1, device.offsetWidth);
        const baseHeight = Math.max(1, device.offsetHeight);
        const screenWidth = baseWidth * (1 - initialScreenInsetX * 2);
        const screenHeight = baseHeight * (1 - initialScreenInsetY * 2);
        const availableHeight = Math.max(1, window.innerHeight - navbarHeight);
        const coverScale = Math.max(
          window.innerWidth / screenWidth,
          availableHeight / screenHeight
        );

        const desiredCenterY =
          navbarHeight + (window.innerHeight - navbarHeight) / 2;
        const layoutCenterY = device.offsetTop + baseHeight / 2;

        return {
          scale: Math.max(1, coverScale * 1.2),
          y: desiredCenterY - layoutCenterY,
        };
      };

      let initialGeometry = getInitialGeometry();
      let trigger = null;

      const applyFinalState = () => {
        timeline.progress(1).pause();
        gsap.set(device, {
          scale: 1,
          y: 0,
          borderRadius: finalRadius,
          clearProps: "transformOrigin",
        });
        gsap.set(screen, {
          inset: finalScreenInset,
          borderRadius: finalScreenRadius,
        });
        gsap.set(frame, { autoAlpha: 1 });
        gsap.set(copy, { autoAlpha: 1, y: 0 });
        gsap.set(badges, { autoAlpha: 1, y: 0 });
        section.classList.add("is-scroll-animation-complete");
        section.classList.remove("is-scroll-animation-active");
      };

      gsap.set(device, {
        scale: reducedMotion ? 1 : initialGeometry.scale,
        y: reducedMotion ? 0 : initialGeometry.y,
        borderRadius: reducedMotion ? finalRadius : "0px",
        transformOrigin: "50% 50%",
        force3D: !lowResource,
      });
      gsap.set(screen, {
        inset: reducedMotion ? finalScreenInset : initialScreenInset,
        borderRadius: reducedMotion ? finalScreenRadius : "80px",
      });
      // Keep the physical frame attached to the video throughout the entire
      // morph. At the fullscreen state it sits just outside the viewport.
      gsap.set(frame, { autoAlpha: 1 });
      gsap.set(copy, {
        autoAlpha: reducedMotion ? 1 : 0,
        y: reducedMotion ? 0 : lowResource ? 12 : 26,
      });
      gsap.set(badges, {
        autoAlpha: reducedMotion ? 1 : 0,
        y: reducedMotion ? 0 : 10,
      });

      const timeline = gsap.timeline({ paused: true });
      timeline
        .to(device, {
          scale: 1,
          y: 0,
          borderRadius: finalRadius,
          duration: lowResource ? 0.7 : 1.05,
          ease: "power3.inOut",
        })
        .to(
          screen,
          {
            inset: finalScreenInset,
            borderRadius: finalScreenRadius,
            duration: lowResource ? 0.7 : 1.05,
            ease: "power3.inOut",
          },
          0
        )
        .to(
          copy,
          {
            autoAlpha: 1,
            y: 0,
            duration: lowResource ? 0.34 : 0.54,
            ease: "power2.out",
          },
          lowResource ? 0.5 : 0.78
        )
        .to(
          badges,
          {
            autoAlpha: 1,
            y: 0,
            duration: lowResource ? 0.22 : 0.34,
            stagger: lowResource ? 0.015 : 0.04,
            ease: "power2.out",
          },
          lowResource ? 0.62 : 0.9
        );

      if (reducedMotion || lowResource) {
        applyFinalState();
        return;
      }

      let maxProgress = 0;
      let completed = false;

      const finishOnce = () => {
        if (completed) return;
        completed = true;
        maxProgress = 1;
        applyFinalState();
        releaseCompletedScrollPin({ trigger, section });
      };

      trigger = ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: () =>
          `+=${Math.round(window.innerHeight * (lowResource ? 0.82 : 1.38))}`,
        pin: true,
        pinSpacing: true,
        anticipatePin: 0,
        invalidateOnRefresh: true,
        refreshPriority: -10,
        preventOverlaps: "captec-home-pinned",

        onEnter: () => {
          section.classList.add("is-scroll-animation-active");
        
          if (videoStartedRef.current) {
            resumeStartedVideo();
          } else {
            playVideoOnce();
          }
        },
        
        onEnterBack: () => {
          if (completed) {
            applyFinalState();
          }
        
          if (videoStartedRef.current) {
            resumeStartedVideo();
          } else {
            playVideoOnce();
          }
        },
        
        onLeave: () => {
          finishOnce();
        
          /*
           * No se pausa el video.
           * Continúa reproduciéndose hasta terminar una sola vez.
           */
        },
        
        onLeaveBack: () => {
          section.classList.remove("is-scroll-animation-active");
        
          /*
           * No se pausa al regresar.
           * El video conserva su reproducción continua.
           */
        },
        
        onRefresh: () => {
          if (!completed && maxProgress <= 0.001) {
            initialGeometry = getInitialGeometry();
        
            gsap.set(device, {
              scale: initialGeometry.scale,
              y: initialGeometry.y,
              borderRadius: "0px",
            });
        
            gsap.set(screen, {
              inset: initialScreenInset,
              borderRadius: "80px",
            });
          }
        
          timeline.progress(maxProgress).pause();
        
          if (completed) {
            applyFinalState();
          }
        },
        
        onUpdate: (self) => {
          /*
           * ScrollTrigger controla únicamente la transformación visual.
           * Ya no pausa ni reanuda el video según velocidad o dirección.
           */
          if (self.progress > maxProgress) {
            maxProgress = self.progress;
            timeline.progress(maxProgress).pause();
          }
        
          if (maxProgress >= 0.999) {
            finishOnce();
          }
        },
      });

      return () => trigger?.kill(true);
    }, section);

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (lowResource) video.pause();
          return;
        }
        if (!videoRequested) {
          videoRequested = true;
          video.preload = "auto";
          video.load();
        }
        if (videoStartedRef.current) {
          resumeStartedVideo();
        } else {
          playVideoOnce();
        }
      },
      {
        rootMargin: "100px 0px",
        threshold: 0.12,
      }
    );
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseForHiddenTab();
        return;
      }
    
      if (videoStartedRef.current) {
        resumeStartedVideo();
      } else if (isSectionVisible()) {
        playVideoOnce();
      }
    };

    visibilityObserver.observe(section);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      visibilityObserver.disconnect();
    
      video.removeEventListener("canplay", handleCanPlay);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    
      video.pause();
      videoStartedRef.current = false;
    
      ctx.revert();
    
      section.classList.remove(
        "is-scroll-animation-active",
        "is-scroll-animation-complete"
      );
    };
  }, [enabled]);

  const handleLoadedData = () => {
    videoAvailableRef.current = true;
    setVideoError(false);
  };

  const handleVideoError = () => {
    videoAvailableRef.current = false;
    setVideoError(true);
  };

  return (
    <section
      ref={sectionRef}
      className="video-morph-section"
      data-testid="video-morph-showcase"
    >
      <div className="video-morph-bg" />
      <div className="video-morph-noise" />

      <div className="video-morph-stage">
        <div className="video-morph-device">
          <div className="video-morph-screen video-morph-screen-under-frame">
          <video
  ref={videoRef}
  src={VIDEO_SRC}
  poster={VIDEO_POSTER}
  muted
  playsInline
  preload="none"
  className="video-morph-video"
  onLoadedData={handleLoadedData}
  onError={handleVideoError}
  disablePictureInPicture
  controlsList="nodownload noplaybackrate noremoteplayback"
/>

            <div
              className={`video-morph-placeholder ${videoError ? "" : "is-hidden"}`}
              aria-hidden="true"
            >
              <span>Vista previa del ecosistema de pagos</span>
            </div>
          </div>

          <img
            src={PHONE_FRAME_SRC}
            alt=""
            aria-hidden="true"
            className="video-morph-frame video-morph-phone-frame-img"
            decoding="async"
          />
        </div>

        <div className="video-morph-copy">
          <div className="video-morph-eyebrow">
            <span />
            Todo conectado
          </div>

          <h2>
            Una sola infraestructura para implementar y operar todo el ciclo de pago.
          </h2>

          <p>
            Del core bancario a la billetera digital. Del cajero al comercio electrónico. CAPTEC implementa, integra, certifica y procesa cada capa del ecosistema de pagos con seguridad y operación 24/7.
          </p>

          <div className="video-morph-badges">
            <div><Server />Procesamiento</div>
            <div><CreditCard />Emisión</div>
            <div><ShieldCheck />Digitalización de tarjetas</div>
            <div><Network />Switch</div>
            <div><KeyRound />Tokenización</div>
            <div><ArrowLeftRight />Transferencias</div>
            <div><Clock3 />Operación 24/7</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoMorphShowcase;
